/**
 * ゲーム進行セッションの永続化（自前・GPL非依存）。
 *
 * 目的: ブラウザがバックグラウンド化→タブ破棄→再アクティブ化で完全リロードしても、
 * また再デプロイ（バージョンアップ）後でも、直近の盤面（球の配置・落球状況）と
 * 進行状態（ブレーク済み/ボールインハンド/連続ラン進行）に加えて、
 * 9ボールのルール状態（手番・フェーズ・連続ファウル・勝敗）を復元できるようにする。
 *
 * バージョン方針（③A: 設定は残し進行のみ安全に破棄）:
 *   - セッションには SCHEMA_VERSION を埋め込む。
 *   - 読み込み時に version 不一致・構造破損を検出したら「黙って破棄」して null を返す
 *     （= 新規ゲーム開始）。設定・記録など他キーには一切触れないため、ユーザー資産は残る。
 *   - これによりデータ構造を変えるアップデートでもクラッシュ・全消えが起きない。
 *
 * 保存先は localStorage（②A）。球十数個の位置/状態のみで軽量なため同期保存で十分。
 */

import type { PlayMode, PersistedBall } from '../sim/world.js';
import type {
  RuleState,
  Phase,
  PlayerId,
  PlayerState,
  WinReason,
} from '../sim/rules/types.js';
import { readJson, writeJson, removeKey } from './storage.js';

const SESSION_KEY = 'pool.session';
/** スキーマ版。データ構造を変える場合は必ずインクリメントする（旧データは破棄される）。 */
const SCHEMA_VERSION = 2;

const VALID_MODES: readonly PlayMode[] = [
  'practice',
  'nineBall',
  'tenBall',
  'eightBall',
];

/** 永続化するゲーム進行セッション。 */
export interface GameSession {
  /** スキーマ版（読み込み時の互換判定に使う）。 */
  readonly v: number;
  /** プレイモード（復元する盤面の種別）。 */
  readonly mode: PlayMode;
  /** 全球の位置・状態（静止時スナップショット）。 */
  readonly balls: PersistedBall[];
  /** ブレーク（最初のショット）済みか。false=まだ配置/ブレーク前。 */
  readonly broken: boolean;
  /** スクラッチ後のボールインハンド中か。 */
  readonly ballInHand: boolean;
  /** 現在ラックがミスなく進行中か（連続ブレイクラン判定の途中状態）。 */
  readonly runAlive: boolean;
  /**
   * 9ボールのルール状態（手番・フェーズ・連続ファウル・勝敗）。
   * ルール非適用モード（practice/10・8ボール）では null。
   */
  readonly rule: RuleState | null;
  /** 保存時刻（epoch ms・診断用）。 */
  readonly ts: number;
}

/** 保存に必要な可変部分（v / ts は saveSession が付与する）。 */
export type SessionInput = Omit<GameSession, 'v' | 'ts'>;

const isFiniteNumber = (n: number): boolean =>
  typeof n === 'number' && Number.isFinite(n);

/** 1球分の構造・数値妥当性を検証する（state 文字列の中身は world 側で検証）。 */
const isValidBall = (b: PersistedBall): boolean =>
  !!b &&
  isFiniteNumber(b.id) &&
  isFiniteNumber(b.x) &&
  isFiniteNumber(b.y) &&
  isFiniteNumber(b.z) &&
  typeof b.state === 'string';

const VALID_PHASES: readonly Phase[] = [
  'breakPlacement',
  'pushOutOption',
  'pushOutResponse',
  'ballInHand',
  'inPlay',
  'gameOver',
];
const VALID_WIN_REASONS: readonly WinReason[] = ['pocket9', 'threeFoul'];

const isPlayerId = (n: number): n is PlayerId => n === 0 || n === 1;

/** プレイヤー1人分のルール状態の妥当性。 */
const isValidPlayerState = (p: PlayerState): boolean =>
  !!p &&
  isFiniteNumber(p.consecutiveFouls) &&
  p.consecutiveFouls >= 0 &&
  typeof p.foulWarned === 'boolean';

/**
 * ルール状態（RuleState）の構造・値の妥当性を検証する。
 * 破損データを reducer に渡すと dispatch が想定外に振る舞うため、復元前に弾く。
 * （export: localStorage 非依存の単体テスト用）
 */
export const isValidRuleState = (r: RuleState): boolean =>
  !!r &&
  r.mode === 'nineBall' &&
  isPlayerId(r.current) &&
  isPlayerId(r.breaker) &&
  VALID_PHASES.includes(r.phase) &&
  typeof r.pushOutDeclared === 'boolean' &&
  Array.isArray(r.players) &&
  r.players.length === 2 &&
  r.players.every(isValidPlayerState) &&
  (r.winner === null || isPlayerId(r.winner)) &&
  (r.winReason === null || VALID_WIN_REASONS.includes(r.winReason));

/**
 * セッションを読み込む。version 不一致・構造破損・モード不正・球データ不正は
 * 「復元対象なし」とみなし null を返す（version 不一致時はキーも掃除する）。
 */
export const loadSession = (): GameSession | null => {
  const data = readJson<GameSession>(SESSION_KEY);
  if (!data || typeof data !== 'object') return null;

  // ③A: 互換性のない版は黙って破棄（設定など他キーは温存）。
  if (data.v !== SCHEMA_VERSION) {
    removeKey(SESSION_KEY);
    return null;
  }
  if (!VALID_MODES.includes(data.mode)) return null;
  if (!Array.isArray(data.balls) || data.balls.length === 0) return null;
  if (!data.balls.every(isValidBall)) return null;
  if (
    typeof data.broken !== 'boolean' ||
    typeof data.ballInHand !== 'boolean' ||
    typeof data.runAlive !== 'boolean'
  ) {
    return null;
  }
  // rule は null（ルール非適用モード）か、構造的に妥当な RuleState のみ許可する。
  // 不正なら局面全体を「復元対象なし」として破棄（部分復元はしない）。
  if (data.rule !== null && !isValidRuleState(data.rule)) return null;
  return data;
};

/** 現在のゲーム進行をセッションとして保存する（version / ts を付与）。 */
export const saveSession = (input: SessionInput): void => {
  const session: GameSession = {
    v: SCHEMA_VERSION,
    ts: Date.now(),
    mode: input.mode,
    balls: input.balls,
    broken: input.broken,
    ballInHand: input.ballInHand,
    runAlive: input.runAlive,
    rule: input.rule,
  };
  writeJson(SESSION_KEY, session);
};

/** セッションを破棄する（明示リセット等で進行を消す場合に使用）。 */
export const clearSession = (): void => {
  removeKey(SESSION_KEY);
};
